"use client";

import { useSearchParams } from "next/navigation";
import { createContext, type Dispatch, type ReactNode, type SetStateAction, useContext, useEffect } from "react";

import { usePersistedStorage } from "@/lib/hooks/use-persisted-storage";

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------

type DistConfigFlagStatus = "on" | "off" | null;

export interface DistConfig {
  verbose: DistConfigFlagStatus;
}

interface DistConfigContext {
  distConfig: DistConfig;
  setDistConfig: Dispatch<SetStateAction<DistConfig>>;
  removeDistConfig: () => void;
}

// ------------------------------------------------------------------
// Defaults
// ------------------------------------------------------------------
const DIST_CONFIG_DEFAULTS: DistConfig = {
  verbose: "off",
};

const STORAGE_KEY = "__agora_dist_config";

// ------------------------------------------------------------------
// Context
// ------------------------------------------------------------------
const DistConfigContext = createContext<DistConfigContext | undefined>(undefined);

export default function DistConfigProvider({ children }: { children: ReactNode }) {
  const searchParams = useSearchParams();
  const verbose = searchParams.get("verbose") as DistConfigFlagStatus;

  const [distConfig, setDistConfig, removeDistConfig] = usePersistedStorage<DistConfig>(
    STORAGE_KEY,
    DIST_CONFIG_DEFAULTS,
    "local",
  );

  useEffect(() => {
    setDistConfig((prev) => {
      const next = {
        ...prev,
        ...(verbose ? { verbose } : {}),
      };

      if (next?.verbose === "on") {
        logConfig(next);
      }

      return next;
    });
  }, [setDistConfig, verbose]);

  return (
    <DistConfigContext.Provider value={{ distConfig, setDistConfig, removeDistConfig }}>
      {children}
    </DistConfigContext.Provider>
  );
}

// ------------------------------------------------------------------
// Hook
// ------------------------------------------------------------------
export function useDistConfig(): DistConfigContext {
  const ctx = useContext(DistConfigContext);
  if (!ctx) throw new Error("useDistConfig must be used within DistConfigProvider");
  return ctx;
}

function logConfig(config: DistConfig) {
  console.log("🔧 Agora distConfig:");
  console.log("   verbose     :", config.verbose ? "✅" : "🚫");
}
